import { Link } from 'react-router-dom'

/**
 * A2P 10DLC SMS consent checkbox.
 *
 * Carrier rules this component exists to satisfy:
 *  - The box is NEVER checked by default.
 *  - The box is NEVER required to submit the form (no `required` attribute,
 *    and callers must not disable submit on it).
 *  - SMS consent is its own checkbox, never bundled with an email opt-in or
 *    with terms acceptance.
 *  - The label carries every required disclosure: who is texting, what about,
 *    that consent is not a condition of purchase, frequency, rates, STOP and
 *    HELP, and live links to the Privacy Policy and Terms of Service.
 *
 * SMS_CONSENT_TEXT is the exact wording shown to the user and is stored
 * alongside every consent record so we can prove later what was agreed to.
 * Bump SMS_CONSENT_VERSION whenever the wording changes.
 */
export const SMS_CONSENT_VERSION = '2026-08-07'

export const SMS_CONSENT_TEXT =
  'I agree to receive text messages from Advantage Horsemanship LLC about clinic ' +
  'reminders, schedule changes, and logistics at the number provided. Consent is not ' +
  'a condition of purchase. Message frequency varies. Message and data rates may ' +
  'apply. Reply STOP to opt out, HELP for help. See our Privacy Policy and Terms of Service.'

interface SmsConsentCheckboxProps {
  checked: boolean
  onChange: (checked: boolean) => void
  id?: string
  /** Text variant: "light" for dark backgrounds, "dark" (default) for light backgrounds */
  variant?: 'light' | 'dark'
}

export function SmsConsentCheckbox({
  checked,
  onChange,
  id = 'sms-consent',
  variant = 'dark',
}: SmsConsentCheckboxProps) {
  const textColor = variant === 'light' ? 'text-white/70' : 'text-navy/70'
  const linkColor = variant === 'light' ? 'text-gold hover:text-gold/80' : 'text-gold hover:text-gold/70'

  return (
    <div className="flex items-start gap-3">
      <input
        id={id}
        name={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 flex-shrink-0 accent-gold cursor-pointer"
        style={{ accentColor: '#C8A84B' }}
      />
      <label htmlFor={id} className={`font-body text-xs leading-relaxed ${textColor} cursor-pointer`}>
        I agree to receive text messages from Advantage Horsemanship LLC about clinic
        reminders, schedule changes, and logistics at the number provided. Consent is not a
        condition of purchase. Message frequency varies. Message and data rates may apply.
        Reply STOP to opt out, HELP for help. See our{' '}
        <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className={`underline ${linkColor}`}>
          Privacy Policy
        </Link>{' '}
        and{' '}
        <Link to="/terms" target="_blank" rel="noopener noreferrer" className={`underline ${linkColor}`}>
          Terms of Service
        </Link>
        .
      </label>
    </div>
  )
}
